/**
 * <lk-value-props> Web Component
 *
 * Pagination dots for the value-props bar's mobile scroll-snap carousel
 * (sections/custom_lk-value-props.liquid). CSS scroll-snap does the actual
 * sliding; this element only keeps the dots in sync with the scroll position
 * and scrolls the track when a dot is pressed. On desktop the track doesn't
 * overflow and the dots are display:none, so this is effectively inert there.
 *
 * connectedCallback/disconnectedCallback fire on shopify:section:load and
 * shopify:section:unload, so listeners are set up and torn down automatically
 * in the theme editor without stacking.
 *
 * Usage (in Liquid):
 *   <lk-value-props>
 *     <ul data-lk-vp-track>
 *       <li data-lk-vp-item>…</li> ×N
 *     </ul>
 *     <div data-lk-vp-dots>
 *       <button data-lk-vp-dot data-index="0" aria-label="…"></button> ×N
 *     </div>
 *   </lk-value-props>
 */

class LkValueProps extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lk-vp-track]');
    this.items = Array.from(this.querySelectorAll('[data-lk-vp-item]'));
    this.dots = Array.from(this.querySelectorAll('[data-lk-vp-dot]'));
    if (!this.track || this.items.length < 2 || this.dots.length === 0) return;

    this.activeIndex = -1;
    this.scrollRaf = null;

    this.onScroll = this.onScroll.bind(this);
    this.onDotClick = this.onDotClick.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

    this.setActive(0);
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.dots) this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  /** Index of the slide nearest the current scroll position. */
  get nearestIndex() {
    const width = this.track.clientWidth;
    if (!width) return 0;
    const index = Math.round(this.track.scrollLeft / width);
    return Math.max(0, Math.min(index, this.items.length - 1));
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.setActive(this.nearestIndex);
    });
  }

  onDotClick(event) {
    const index = Number(event.currentTarget.dataset.index || 0);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: index * this.track.clientWidth,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('lk-value-props')) {
  customElements.define('lk-value-props', LkValueProps);
}
